import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState } from './store';
import {
  login,
  logout,
  updateFranchiseId,
} from '../features/auth/authSlice';
import {
  BrandSource,
  analyticsBrand,
  identifyUser,
  resetAnalyticsIdentity,
  resolveBrand,
} from '../util/analytics';

/**
 * Mixpanel identity follows the auth slice, for every tenant and every kind
 * of account (customer, franchisee, manager, technician):
 *
 * - `login` (Login, SignUp, ForgotPassword, and the account created with a
 *   booking on the Schedule step) and `updateFranchiseId` -> identifyUser,
 *   with the database user id as the distinct id.
 * - `logout` (the menu, or an expired session) -> mixpanel.reset().
 *
 * A saved session is identified at start-up by identifyFromSession, called
 * from index.tsx once the tenant (and so the brand) is known.
 */
export const identityAnalyticsListener = createListenerMiddleware();

identityAnalyticsListener.startListening({
  matcher: isAnyOf(login, updateFranchiseId),
  effect: (_action, api) => {
    const state = api.getState() as RootState;
    // The brand bootTheme set from the tenant record (preset / subdomain);
    // the tenant slice only holds a name, which is not a brand key.
    identifyUser(state.auth?.currentUser, analyticsBrand());
  },
});

identityAnalyticsListener.startListening({
  actionCreator: logout,
  effect: () => {
    resetAnalyticsIdentity();
  },
});

/** App load with a saved session: identify it. */
export function identifyFromSession(
  state: Pick<RootState, 'auth' | 'tenant'>,
  tenant?: BrandSource | null,
): boolean {
  if (!state?.auth?.isAuthenticated) return false;
  return identifyUser(
    state.auth.currentUser,
    tenant ? resolveBrand(tenant) : analyticsBrand(),
  );
}
